import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

import { ReportChromeService } from '../report-chrome.service';
import { reportDuration } from '../report-duration';
import { ReportFmtPipe, ReportTPipe } from '../report-i18n.pipe';
import { ReportPrintService } from '../report-print.service';
import { ReportView } from '../report.model';

/** Une entrée de la barre de matières, prête à afficher. */
interface SubjectNavEntry {
  /** `null` pour l'entrée « toutes les matières ». */
  id: number | null;
  label: string;
  /** Absent tant que la matière n'a pas été diagnostiquée. */
  percentage: number | null;
  /** Classes de la pastille de palier, modificateur compris. */
  dotClass: string;
}

/**
 * En-tête du rapport : identité de l'élève, métadonnées du test, actions.
 *
 * ── Deux commandes retirées de la maquette ─────────────────────────────────
 * Le document autonome portait sa propre bascule de thème et son propre
 * sélecteur de langue, avec son `localStorage`. Devenu écran de l'application,
 * le rapport n'a plus à en décider : l'application possède déjà les deux, et
 * deux commandes pour un même réglage finissent par diverger — l'utilisateur
 * ne sait plus laquelle il vient d'actionner. Elles sont donc supprimées ; le
 * rapport se contente de **suivre** le thème et la langue (voir
 * {@link ReportChromeService}).
 *
 * Le bouton « télécharger le PDF » disparaît pour la même raison de sincérité :
 * il n'existe pas de seconde composition. Le PDF **est** cette page imprimée
 * (voir {@link ReportPrintService}) — deux boutons pour un seul geste
 * laisseraient croire à deux documents.
 *
 * ── Hôte en `display: contents` ────────────────────────────────────────────
 * Indispensable ici, et pas seulement par cohérence : `.report-header` est en
 * `position: sticky`. Un élément de composant intercalé deviendrait son bloc
 * conteneur, à la hauteur exacte de l'en-tête — qui cesserait donc de coller
 * dès le premier pixel de défilement.
 */
@Component({
  selector: 's2e-report-header',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReportFmtPipe, ReportTPipe, TranslateModule],
  templateUrl: './report-header.component.html',
  host: { '[style.display]': "'contents'" },
})
export class ReportHeaderComponent {
  protected readonly chrome = inject(ReportChromeService);
  protected readonly print = inject(ReportPrintService);

  readonly view = input.required<ReportView>();
  /** Panneau de matière actuellement ouvert ; `null` avant tout choix. */
  readonly activeSubjectId = input<number | null>(null);

  readonly subjectSelected = output<number | null>();
  readonly backRequested = output<void>();

  /** Durée de la séance, dans l'unité qui lui convient. Voir {@link reportDuration}. */
  protected readonly duration = computed(() =>
    reportDuration(this.view().header.durationMinutes),
  );

  /**
   * Pastille « Séance n ».
   *
   * `sessionCount` compte les séances **couvertes par le document** : au plus
   * une en portée matière, toutes celles du rapport complet sinon. Rien à
   * afficher quand aucune n'a été passée — le rapport se réduit alors à ce
   * qu'il reste à faire.
   */
  protected readonly sessionVars = computed(() => ({
    number: { n: this.view().header.sessionCount },
  }));

  /**
   * Sens de la flèche de retour.
   *
   * « Revenir » va vers le début de la lecture : à gauche en latin, à droite en
   * arabe. La classe Font Awesome est choisie ici plutôt que miroitée en CSS,
   * l'icône étant un glyphe et non une boîte.
   */
  protected readonly backIcon = computed(() =>
    this.chrome.rtl() ? 'fa-solid fa-arrow-right' : 'fa-solid fa-arrow-left',
  );

  /**
   * Barre de matières — absente de la maquette, qui n'en connaissait qu'une.
   *
   * Les matières d'abord, dans l'ordre du modèle, puis l'entrée « toutes les
   * matières » : celle-ci ne change pas de panneau, elle mène aux deux blocs
   * d'ensemble qui ferment le document, et sa place est donc à la fin.
   */
  protected readonly subjectNav = computed<SubjectNavEntry[]>(() => {
    const options = this.view().subjectOptions;
    const subjects = options.filter((option) => option.id !== null);
    const all = options.filter((option) => option.id === null);

    return [...subjects, ...all].map((option) => ({
      id: option.id,
      label: option.label,
      percentage: option.percentage,
      /* Pas de palier — matière non diagnostiquée, ou entrée d'ensemble : la
         pastille reste neutre. Un modificateur de palier inventé peindrait un
         niveau que personne n'a mesuré. */
      dotClass:
        'report-subjectnav__dot' +
        (option.level ? ` report-subjectnav__dot--${option.level}` : ''),
    }));
  });

  /** La barre n'a de sens qu'au-delà d'une matière. */
  protected readonly showSubjectNav = computed(
    () => this.view().scope === 'all' && this.view().subjects.length > 1,
  );

  /**
   * Impression.
   *
   * Le rapport complet s'imprime en entier : à l'écran on ne lit qu'un panneau,
   * mais un PDF « toutes matières » amputé des matières non consultées serait
   * un autre document.
   */
  protected startPrint(): void {
    void this.print.print(this.view().scope === 'all' ? 'all' : 'current');
  }
}
